import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import type {
  UseFormClearErrors,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormSetError,
  UseFormTrigger,
} from 'react-hook-form';

import translations from '../../../../../../../../lang';
import { isAbortError } from '../../../../../../../../utils/fetchJson';
import { useValidateTrialEmailMutation } from '../../../../api/hooks/useValidateTrialEmailMutation';
import { TrialStartApiError } from '../../../../api/request';
import type { TrialStartFormValues } from '../../../../schema/schema';

const { TRIAL_START } = translations['en-US'];

type EmailCheckResult = {
  email: string;
  valid: boolean;
};

type UseTrialEmailValidationParams = {
  setError: UseFormSetError<TrialStartFormValues>;
  clearErrors: UseFormClearErrors<TrialStartFormValues>;
  getValues: UseFormGetValues<TrialStartFormValues>;
  getFieldState: UseFormGetFieldState<TrialStartFormValues>;
  trigger: UseFormTrigger<TrialStartFormValues>;
};

export const useTrialEmailValidation = ({
  setError,
  clearErrors,
  getValues,
  getFieldState,
  trigger,
}: UseTrialEmailValidationParams) => {
  const {
    mutateAsync,
    isLoading: isEmailChecking,
    reset,
  } = useValidateTrialEmailMutation();
  const [lastEmailCheck, setLastEmailCheck] = useState<EmailCheckResult | null>(
    null
  );
  const emailAbortRef = useRef<AbortController | null>(null);

  useEffect(() => () => emailAbortRef.current?.abort(), []);

  const runEmailAvailabilityCheck = async (
    rawEmail: string
  ): Promise<boolean> => {
    const email = rawEmail.trim();

    if (!email) return false;

    if (lastEmailCheck?.email === email) {
      if (!lastEmailCheck.valid) return false;

      return true;
    }

    emailAbortRef.current?.abort();
    const controller = new AbortController();
    emailAbortRef.current = controller;

    try {
      const result = await mutateAsync({ email, signal: controller.signal });

      if (controller.signal.aborted) return false;

      setLastEmailCheck({ email, valid: result.valid });

      if (!result.valid) {
        setError('email', {
          type: 'server',
          message: TRIAL_START.ERRORS.EMAIL_BUSINESS,
        });
        return false;
      }

      clearErrors('email');
      return true;
    } catch (error) {
      if (isAbortError(error)) return false;

      const message =
        error instanceof TrialStartApiError
          ? error.message
          : TRIAL_START.ERRORS.EMAIL_VALIDATE_FAILED;

      setLastEmailCheck({ email, valid: false });
      setError('email', {
        type: 'server',
        message,
      });
      return false;
    } finally {
      if (emailAbortRef.current === controller) {
        emailAbortRef.current = null;
      }
    }
  };

  const handleEmailChange = (
    event: ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const next = event.target.value;
    fieldOnChange(event);

    if (lastEmailCheck && next.trim() !== lastEmailCheck.email) {
      setLastEmailCheck(null);
      reset();
      const emailError = getFieldState('email').error;
      if (emailError?.type === 'server') {
        clearErrors('email');
      }
    }
  };

  const handleEmailBlur = async (fieldOnBlur: () => void) => {
    fieldOnBlur();

    const isFormatValid = await trigger('email');
    if (!isFormatValid) return;

    const email = getValues('email');
    if (!email?.trim()) return;

    await runEmailAvailabilityCheck(email);
  };

  const ensureEmailValidForSubmit = async (
    rawEmail: string
  ): Promise<boolean> => {
    const email = rawEmail.trim();
    const alreadyValid =
      lastEmailCheck?.email === email && lastEmailCheck.valid === true;

    if (alreadyValid) return true;

    return runEmailAvailabilityCheck(email);
  };

  const isEmailVerified = lastEmailCheck?.valid === true;

  return {
    isEmailChecking,
    isEmailVerified,
    handleEmailChange,
    handleEmailBlur,
    ensureEmailValidForSubmit,
  };
};
